import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import ActiveRecord from 'model/activeRecord'
import Member from 'model/person/member'
import AsSoonAsPossible from 'model/appointments/timeSlots/asSoonAsPossible'
import CanceledTime from 'model/appointments/timeSlots/canceled'
import Suggested from 'model/appointments/timeSlots/suggested'
import Confirmed from 'model/appointments/timeSlots/confirmed'
import Pending from 'model/appointments/timeSlots/pending'
import SpecificTime from 'model/appointments/timeSlots/specificTime'
import TimeBlock from 'model/appointments/timeSlots/timeBlock'
import BaseCost from 'model/appointments/baseCost'
import {
  ASAP,
  CANCELED_TIME,
  SPECIFIC_TIME,
  SUGGESTED,
  CONFIRMED,
  PENDING,
  TIME_BLOCK
} from 'constants/appointments'
import { getDateFormatYMD } from 'utils/helpers/dateHelper'
import { Appointment_Status } from 'generated/types'

const enumResolver = entity => {
  // console.log('enumResolver', entity)
  if (isNil(entity)) return null
  const typename = entity.__typename
  switch (typename) {
    case ASAP:
      return new AsSoonAsPossible(entity)
    case CANCELED_TIME:
      return new CanceledTime(entity)
    case SPECIFIC_TIME:
      return new SpecificTime(entity)
    case SUGGESTED:
      return new Suggested(entity)
    case CONFIRMED:
      return new Confirmed(entity)
    case PENDING:
      return new Pending(entity)
    case TIME_BLOCK:
      return new TimeBlock(entity)
    default:
      return new SpecificTime(entity)
  }
}

class Appointment extends ActiveRecord {
  // named after the GraphQL scheme
  constructor(init) {
    super(init)
    // console.log('> Appointment constructor -', init)
    if (isNil(init)) return undefined
    this._archived = init.archived
    this._assigned_entity = init.assigned_entity
    this._cost = new BaseCost(init.cost)
    this._member = new Member(init.member)
    this._member_medical_insurance_plan = init.member_medical_insurance_plan
    this._first_time_visit = init.first_time_visit
    this._preferred_address = init.preferred_address
    this._request_notes = init.request_notes
    this._roadmap = init.roadmap
    this._saved_address = init.saved_address
    this._status = init.appointment_status
    this._ticket = init.ticket
    this._time_slots = init.time_slots
      ? init.time_slots.map(item => enumResolver(item))
      : []
  }

  static shape = {
    id: PropTypes.string.isRequired,
    assigned_entity: PropTypes.object,
    archived: PropTypes.bool,
    status: PropTypes.string,
    time_slots: PropTypes.array.isRequired,
    cost: PropTypes.instanceOf(BaseCost).isRequired,
    member: PropTypes.instanceOf(Member),
    member_medical_insurance_plan: PropTypes.object,
    first_time_visit: PropTypes.bool,
    preferred_address: PropTypes.string,
    request_notes: PropTypes.string,
    saved_address: PropTypes.object
    // roadmap: PropTypes.instanceOf(СarePlan),
  }

  // Getters and Setters
  get archived() {
    return this._archived
  }

  get assigned_entity() {
    return this._assigned_entity
  }

  get cost() {
    return this._cost
  }

  get member() {
    return this._member
  }

  get member_medical_insurance_plan() {
    return this._member_medical_insurance_plan
  }

  get first_time_visit() {
    return this._first_time_visit
  }

  get preferred_address() {
    return this._preferred_address
  }

  get request_notes() {
    return this._request_notes
  }

  get roadmap() {
    return this._roadmap
  }

  get saved_address() {
    return this._saved_address
  }

  get status() {
    return this._status || ''
  }

  get ticket() {
    return this._ticket
  }

  get time_slots() {
    return this._time_slots || []
  }

  set time_slots(timeSlots) {
    this._time_slots = timeSlots
  }

  // Calculable
  get isAppointment() {
    return !!this.id
  }

  get isSuggested() {
    return this._status === Appointment_Status.Suggested
  }

  get isPending() {
    return this._status === Appointment_Status.Pending
  }

  get isPendingCancelled() {
    return this._status === Appointment_Status.PendingCancelled
  }

  get isConfirmed() {
    return this._status === Appointment_Status.Confirmed
  }

  get isCanceled() {
    return this._status === Appointment_Status.Cancelled
  }

  get isCompleted() {
    return this._status === Appointment_Status.Completed
  }

  get isRejected() {
    return this._status === Appointment_Status.Rejected
  }

  // Legacy statuses, refer to
  // https://rightwayhealthcare.atlassian.net/wiki/spaces/PROD/pages/896630916/States+Time-Slots
  get isPendingStatus() {
    return (
      this.isPending &&
      !(this.hasSuggestedSlot || this.hasConfirmedSlot || this.hasCanceledSlot)
    )
  }

  get isInProgressStatus() {
    return (
      (this.isSuggested && this.hasSuggestedSlot) ||
      (this.isPending && this.hasPendingSlot) ||
      (this.isPendingCancelled && this.hasCanceledSlot)
    )
  }

  get isResolvedStatus() {
    return (
      (this.isConfirmed && this.hasConfirmedSlot) ||
      (this.isCompleted && this.hasConfirmedSlot) ||
      this.isRejected
    )
  }
  // -----

  // Helpers
  static getTimeSlotBoundaries = (date, time_start, time_end = time_start) => {
    // Time at at midnight
    const mmtMidnight = time_start.clone().startOf('day')
    // Difference in seconds because we can
    const start = time_start.diff(mmtMidnight, 'seconds')
    const start_time = time_start.format('HH:mm:ss')
    const end = time_end.diff(mmtMidnight, 'seconds')
    const end_time = time_end.format('HH:mm:ss')

    return {
      ...(date && { date: getDateFormatYMD(date) }),
      start,
      start_time,
      end,
      end_time
    }
  }

  get displayStatusText() {
    if (!this.status) return false
    if (this.archived) return 'This Appointment is archived.'
    let result = ''

    if (this.isPendingStatus) {
      result = 'This Appointment is Pending. '
      if (this.hasPendingSlot) result += 'An appointment needs to be confirmed.'
    }
    if (this.isInProgressStatus) {
      result = 'This Appointment is In Progress. '
      if (this.hasSuggestedSlot) result += 'An appointment has been suggested.'
      if (this.hasPendingSlot) result += 'An appointment needs to be confirmed.'
      if (this.hasCanceledSlot)
        result += 'A cancellation request has been received.'
    }
    if (this.isResolvedStatus) {
      result = 'This Appointment is Resolved. '
      if (this.hasConfirmedSlot) result += 'An appointment has been booked.'
    }
    if (this.isRejected) {
      result = 'This Appointment has been rejected.'
    }

    return result
  }

  get hasAsapSlot() {
    return !!this.time_slots.find(slot => slot.isAsap && !slot.delete)
  }

  get hasSpecificSlot() {
    return !!this.time_slots.find(slot => slot.isSpecific && !slot.delete)
  }

  get hasTimeBlockSlot() {
    return !!this.time_slots.find(slot => slot.isTimeBlock && !slot.delete)
  }

  get hasSuggestedSlot() {
    return !!this.time_slots.find(slot => slot.isSuggested)
  }

  get hasPendingSlot() {
    return !!this.time_slots.find(slot => slot.isPending)
  }

  get hasCanceledSlot() {
    return !!this.time_slots.find(slot => slot.isCanceledTime)
  }

  get hasConfirmedSlot() {
    return !!this.time_slots.find(slot => slot.isConfirmed)
  }

  // Non-requested means suggested by navigator, one of the possible types
  get hasNonRequestedSlots() {
    return !!this.time_slots.find(slot => !slot.isRequested)
  }

  get getAsapSlot() {
    return this.time_slots.find(slot => slot.isAsap && !slot.delete) || false
  }

  get getSpecificSlots() {
    return (
      this.time_slots.filter(slot => slot.isSpecific && !slot.delete) || false
    )
  }

  get getTimeBlockSlots() {
    return (
      this.time_slots.filter(slot => slot.isTimeBlock && !slot.delete) || false
    )
  }

  get getNonRequestedSlots() {
    return this.time_slots.filter(slot => !slot.isRequested)
  }

  get shouldAllowSuggest() {
    return (
      !this.archived &&
      !this.hasSuggestedSlot &&
      !this.hasConfirmedSlot &&
      !this.hasPendingSlot
    )
  }

  get shouldBlockUI() {
    return (
      this.archived ||
      (this.status.length > 0 && !this.isPending) ||
      this.hasPendingSlot
    )
  }

  get shouldBlockReadyToClose() {
    return !this.isResolvedStatus && !this.archived
  }
}

export default Appointment
